import { useEffect, useState, useCallback } from 'react';
import { Eye, X, Mail, Phone, Building, Package, User, Calendar, MessageSquare, Loader2, RefreshCw } from 'lucide-react';
import { inquiriesApi } from '@/services/api';
import type { InquiryStatus } from '@/types';
import { toast } from 'sonner';

/**
 * Extended Inquiry type with product relation
 */
interface InquiryWithProduct {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  productId?: string;
  productName?: string;
  product?: { id: string; title: string; slug: string };
  status: InquiryStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface Stats {
  total: number;
  byStatus: Record<string, number>;
}

interface PaginatedMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Status configuration with labels and colors
 */
const STATUS_CONFIG: Record<InquiryStatus, { label: string; color: string }> = {
  NEW: { label: 'Yeni', color: 'bg-blue-100 text-blue-800' },
  CONTACTED: { label: 'İletişime Geçildi', color: 'bg-yellow-100 text-yellow-800' },
  QUALIFIED: { label: 'Nitelikli', color: 'bg-green-100 text-green-800' },
  CLOSED: { label: 'Kapatıldı', color: 'bg-gray-100 text-gray-800' },
};

/**
 * InquiriesPage - Admin Inquiries Management
 *
 * Features:
 * - Filter by status
 * - Paginated list
 * - Detail modal with status update
 * - Admin notes
 */
export const InquiriesPage = () => {
  // Data state
  const [inquiries, setInquiries] = useState<InquiryWithProduct[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryWithProduct | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Filters & pagination
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<PaginatedMeta | null>(null);

  // Edit state
  const [editStatus, setEditStatus] = useState<InquiryStatus>('NEW');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  /**
   * Load inquiries and stats
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [inquiriesResponse, statsData] = await Promise.all([
        inquiriesApi.getAll({
          status: statusFilter || undefined,
          page,
          limit: 20,
        }),
        inquiriesApi.getStats(),
      ]);

      setInquiries(inquiriesResponse.data || []);
      setMeta(inquiriesResponse.meta);
      setStats(statsData);
    } catch (err) {
      console.error('Veri yüklenemedi:', err);
      setError(err instanceof Error ? err.message : 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Open detail modal
   */
  const openDetail = (inquiry: InquiryWithProduct) => {
    setSelectedInquiry(inquiry);
    setEditStatus(inquiry.status);
    setEditNotes(inquiry.notes || '');
    setModalOpen(true);
  };

  /**
   * Close modal
   */
  const closeModal = () => {
    setModalOpen(false);
    setSelectedInquiry(null);
  };

  /**
   * Save changes
   */
  const handleSave = async () => {
    if (!selectedInquiry) return;

    setSaving(true);
    try {
      await inquiriesApi.update(selectedInquiry.id, {
        status: editStatus,
        notes: editNotes,
      });
      toast.success('Talep başarıyla güncellendi');
      closeModal();
      loadData();
    } catch (err) {
      toast.error('Güncelleme başarısız');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (newFilter: string) => {
    setStatusFilter(newFilter);
    setPage(1);
  };

  const totalPages = meta?.totalPages || 1;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Talepler</h1>
          <p className="text-sm text-gray-500 mt-1">
            Müşteri taleplerini yönetin ve takip edin
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Refresh Button */}
          <button
            onClick={() => loadData()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </button>

          {/* Stats Summary */}
          {stats && (
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{stats.total}</span>
              <span className="text-gray-500">Toplam</span>
            </div>
            {stats.byStatus?.NEW > 0 && (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="font-medium text-blue-600">{stats.byStatus.NEW}</span>
                <span className="text-gray-500">Yeni</span>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => handleFilterChange('')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            statusFilter === ''
              ? 'bg-black text-white'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          Tümü ({stats?.total || 0})
        </button>
        {(Object.entries(STATUS_CONFIG) as [InquiryStatus, { label: string; color: string }][]).map(
          ([key, config]) => (
            <button
              key={key}
              onClick={() => handleFilterChange(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                statusFilter === key
                  ? 'bg-black text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {config.label} ({stats?.byStatus?.[key] || 0})
            </button>
          )
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && inquiries.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Talep bulunamadı</h3>
          <p className="text-gray-500">
            {statusFilter
              ? `"${STATUS_CONFIG[statusFilter as InquiryStatus]?.label}" durumunda talep yok`
              : 'Henüz talep gelmemiş'}
          </p>
        </div>
      )}

      {/* Inquiries Table */}
      {!loading && !error && inquiries.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Müşteri
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    İletişim
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Ürün
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Tarih
                  </th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {inquiries.map((inquiry) => (
                  <tr
                    key={inquiry.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* Customer Name & Company */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {inquiry.name}
                          </p>
                          {inquiry.company && (
                            <p className="text-sm text-gray-500 truncate">
                              {inquiry.company}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Contact Info */}
                    <td className="px-6 py-4 hidden md:table-cell">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[200px]">
                            {inquiry.email}
                          </span>
                        </div>
                        {inquiry.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{inquiry.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Product */}
                    <td className="px-6 py-4 hidden lg:table-cell">
                      {inquiry.product?.title || inquiry.productName ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[150px]">
                            {inquiry.product?.title || inquiry.productName}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          STATUS_CONFIG[inquiry.status]?.color ||
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {STATUS_CONFIG[inquiry.status]?.label || inquiry.status}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{formatDate(inquiry.createdAt)}</span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openDetail(inquiry)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">Detay</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50">
              <span className="text-sm text-gray-500">
                Sayfa {page} / {totalPages} ({meta.total} kayıt)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                >
                  Önceki
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                >
                  Sonraki
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {modalOpen && selectedInquiry && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Talep Detayı
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {formatDate(selectedInquiry.createdAt)}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Müşteri Bilgileri
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">İsim</p>
                      <p className="font-medium text-gray-900">
                        {selectedInquiry.name}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Email</p>
                      <a
                        href={`mailto:${selectedInquiry.email}`}
                        className="font-medium text-blue-600 hover:underline truncate block"
                      >
                        {selectedInquiry.email}
                      </a>
                    </div>
                  </div>

                  {selectedInquiry.phone && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Telefon</p>
                        <a
                          href={`tel:${selectedInquiry.phone}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {selectedInquiry.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {selectedInquiry.company && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Building className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Şirket</p>
                        <p className="font-medium text-gray-900">
                          {selectedInquiry.company}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Mesaj
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {selectedInquiry.message}
                  </p>
                </div>
              </div>

              {/* Product */}
              {(selectedInquiry.product || selectedInquiry.productName) && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    İlgili Ürün
                  </h3>
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Package className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedInquiry.product?.title ||
                          selectedInquiry.productName}
                      </p>
                      {selectedInquiry.product?.slug && (
                        <a
                          href={`/products/${selectedInquiry.product.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Ürünü Gör
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Status Update */}
              <div>
                <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Durum
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as InquiryStatus)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow"
                >
                  {(
                    Object.entries(STATUS_CONFIG) as [
                      InquiryStatus,
                      { label: string; color: string }
                    ][]
                  ).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Admin Notları
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow resize-none"
                  placeholder="İç notlar ekleyin..."
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 text-sm font-medium bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  'Kaydet'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiriesPage;
